/**
 * @file status-pill.component.ts
 * @description Uppercase mini status pill for knowledge-base and document
 * rows. Four canonical states; the `indexing` variant adds a 6px pulsing
 * dot. Used across `/references/manage` (row right-rail) and the KB
 * detail page header.
 *
 * Visual spec: docs/design_handoff_references_redesign/README.md §2 (KB row).
 */
import { ChangeDetectionStrategy, Component, Input, computed, signal } from '@angular/core';
import { CommonModule } from '@angular/common';

export type StatusPillKind =
  | 'ready'
  | 'indexing'
  | 'empty'
  | 'failed'
  | 'stage-egg'
  | 'stage-caterpillar'
  | 'stage-chrysalis'
  | 'stage-butterfly';

@Component({
  selector: 'app-status-pill',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './status-pill.component.html',
  styleUrls: ['./status-pill.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StatusPillComponent {
  readonly #status = signal<StatusPillKind>('empty');
  readonly #count = signal<number | null>(null);
  readonly #total = signal<number | null>(null);
  readonly #labelOverride = signal<string | null>(null);

  /** Visual state of the pill. */
  @Input({ required: true }) set status(value: StatusPillKind) {
    this.#status.set(value);
  }
  get status(): StatusPillKind {
    return this.#status();
  }

  /** Numerator for `indexing` and `failed` counts. */
  @Input() set count(value: number | null | undefined) {
    this.#count.set(value ?? null);
  }

  /** Denominator for the `indexing` progress display. */
  @Input() set total(value: number | null | undefined) {
    this.#total.set(value ?? null);
  }

  /** Optional label override — `Ready` ↔ `Indexed` share the same colors. */
  @Input() set label(value: string | null | undefined) {
    this.#labelOverride.set(value ?? null);
  }

  readonly resolvedLabel = computed<string>(() => {
    const override = this.#labelOverride();
    if (override) return override;

    const count = this.#count();
    const total = this.#total();
    switch (this.#status()) {
      case 'ready':
        return 'Ready';
      case 'indexing':
        return count != null && total != null ? `Indexing · ${count} / ${total}` : 'Indexing';
      case 'empty':
        return 'Empty';
      case 'failed':
        return count != null ? `Needs review · ${count}` : 'Needs review';
      case 'stage-egg':
        return 'Egg';
      case 'stage-caterpillar':
        return 'Caterpillar';
      case 'stage-chrysalis':
        return 'Chrysalis';
      case 'stage-butterfly':
        return 'Butterfly';
    }
  });
}
